/* eslint-disable no-console */
import {AxiosResponse} from 'axios';
import {SORT} from '../incase-domain/common/sort.enum';
import {ISendPresentationsNotification, ISharePresentationsNotification} from '../incase-domain/notification';
import {PlayerLabel} from '../incase-domain/playerLabel';
import {IElement} from '../incase-domain/elements';
import {IPresentation, IPresentationMeta, PresentationsRequestStatus} from '../incase-domain/presentation';
import {IPaginatedResult, IPaginatedSearch} from '../incase-domain/search';
import {ISlide} from '../incase-domain/slide';
import {httpDelete, httpGet, httpGetWithHeaders, httpPatch, httpPost, httpPut} from './http.service';
import {ProtectionInfo} from '../incase-domain/presentationProtection';
import {AIPresentationMetaData} from '../incase-domain/AIPresentationMetaData';
import {listenToJob} from './job.service';

const BASE_PRESENTATIONS_URL = 'editor/presentations';

export const getPresentationProtection = (presId: string): Promise<ProtectionInfo> => {
    return httpGet(`${BASE_PRESENTATIONS_URL}/${presId}/protection`).catch((error) => {
        console.log(error);
    });
};

export const updatePresentationProtection = (presId: string, protection: ProtectionInfo): Promise<void> => {
    return httpPut(`${BASE_PRESENTATIONS_URL}/${presId}/protection`, protection);
};

export const fetchPresentationFeed = (paginatedSearch: IPaginatedSearch): Promise<IPaginatedResult<IPresentation>> => {
    const {pageNumber, pageSize, ...searchObj} = paginatedSearch;
    return httpPost(`${BASE_PRESENTATIONS_URL}/feed/${pageSize}/${pageNumber}`, searchObj).catch((error) => {
        console.log(error);
    });
};

export const fetchPresentationListPaginated = (
    pageNumber: number,
    pageSize: number,
    searchTerm?: string,
    sortBy?: Record<string, SORT>,
    includeSharedOnes?: boolean,
    includeFirstSlidePreview?: boolean,
    folderId?: string
): Promise<IPaginatedResult<IPresentation>> => {
    let params = {};

    if (searchTerm) params = {...params, searchTerm};
    if (sortBy) params = {...params, ...sortBy};
    if (includeSharedOnes) params = {...params, includeSharedOnes};
    if (includeFirstSlidePreview) params = {...params, includeFirstSlidePreview};
    if (folderId) params = {...params, folderId};

    return httpGet(`${BASE_PRESENTATIONS_URL}/all/${pageSize}/${pageNumber}`, {params}).catch((error) => {
        console.log(error);
    });
};

export const fetchPresentationVerbose = (presentationId: string): Promise<IPresentation> => {
    return httpGet(`${BASE_PRESENTATIONS_URL}/${presentationId}`);
};

export const downloadPresentationVerbose = (presentationId: string): Promise<AxiosResponse<Blob>> => {
    return httpGetWithHeaders(`${BASE_PRESENTATIONS_URL}/${presentationId}/download`, {
        responseType: 'blob',
    });
};

export const importPresentation = (presentation: IPresentation): Promise<IPresentation> => {
    return httpPost(`${BASE_PRESENTATIONS_URL}/import`, {presentation});
};

export const importHtmlPresentation = (slides: {name: string; html: string}[]): Promise<string> => {
    return httpPost(`${BASE_PRESENTATIONS_URL}/import-html`, {slides});
};

export const parseHtmlInteractiveSlots = (html: string): Promise<{html: string; elements: IElement[]}> => {
    return httpPost(`${BASE_PRESENTATIONS_URL}/parse-html-interactive`, {html});
};

export const importPptPresentation = (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    return httpPost(`${BASE_PRESENTATIONS_URL}/import-ppt`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

export const createPresentation = (name?: string, empty?: boolean): Promise<IPresentation> => {
    return httpPost(`${BASE_PRESENTATIONS_URL}`, {name, empty});
};

export const updatePresentation = (
    presentation: IPresentationMeta,
    isSlideUpdate?: boolean
): Promise<
    IPresentationMeta & {
        errors: Record<string, string[]>;
    }
> => {
    return httpPut(`${BASE_PRESENTATIONS_URL}/update`, {presentation, isSlideUpdate});
};

export const duplicatePresentation = (
    presentationId: string,
    isReadOnly?: boolean
): Promise<
    IPresentation & {
        errors: Record<string, unknown>;
    }
> => {
    return httpPost(`${BASE_PRESENTATIONS_URL}/${presentationId}/duplicate`, {isReadOnly}).catch((error) => {
        return {errors: error.response.data};
    });
};

export const removePresentations = (presentations: IPresentation[]): Promise<IPresentation[]> => {
    return httpDelete(`${BASE_PRESENTATIONS_URL}`, presentations).catch((error) => {
        if (error.response && error.response.data && error.response.status === 403) {
            throw error.response.data;
        }
        return {errors: error.response.data};
    });
};

export const updatePlayerLabels = (
    labels: PlayerLabel[],
    presentationId: string
): Promise<{data?: PlayerLabel[]; errors?: {errorMessage: string; data: string[]}}> => {
    return httpPut(`${BASE_PRESENTATIONS_URL}/${presentationId}/player-labels`, labels).catch((error) => {
        console.log(error);
        return error.response.data;
    });
};

export const sendPresentations = (
    recipientId: string,
    presentationIds: string[]
): Promise<{
    success: boolean;
}> => {
    return httpPost(`${BASE_PRESENTATIONS_URL}/send`, {recipientId, presentationIds})
        .then(() => {
            return {success: true};
        })
        .catch((error) => {
            console.log(error);
            return error.response.data;
        });
};

export const sharePresentations = (
    recipientId: string,
    presentationIds: string[],
    isReadOnly: boolean
): Promise<{
    success: boolean;
}> => {
    return httpPost(`${BASE_PRESENTATIONS_URL}/share`, {recipientId, presentationIds, isReadOnly})
        .then(() => {
            return {success: true};
        })
        .catch((error) => {
            console.log(error);
            return error.response.data;
        });
};

export const updateSendPresentationsRequest = (
    notification: ISendPresentationsNotification,
    status: PresentationsRequestStatus
): Promise<boolean> => {
    return httpPost(`${BASE_PRESENTATIONS_URL}/send/update-request`, {notification, status});
};

export const updateSharePresentationsRequest = (
    notification: ISharePresentationsNotification,
    status: PresentationsRequestStatus
): Promise<boolean> => {
    return httpPost(`${BASE_PRESENTATIONS_URL}/share/update-request`, {notification, status});
};

export const fetchPresentationSlides = (
    presentationId: string,
    size: number,
    page: number,
    findBookmarked?: boolean
): Promise<IPaginatedResult<ISlide>> => {
    const params = {findBookmarked};
    return httpGet(`${BASE_PRESENTATIONS_URL}/${presentationId}/slides/${page}/${size}/`, {params});
};

export const fetchSharedPresentationsPaginated = (
    pageNumber: number,
    pageSize: number,
    searchTerm?: string,
    sortBy?: Record<string, SORT>
): Promise<IPaginatedResult<IPresentation>> => {
    let params = {};

    if (searchTerm) params = {...params, searchTerm};
    if (sortBy) params = {...params, ...sortBy};
    return httpGet(`${BASE_PRESENTATIONS_URL}/shared/${pageSize}/${pageNumber}`, {params}).catch((error) => {
        console.log(error);
    });
};

export const removeSharedPresentation = (
    presentationId: string,
    userEmail: string
): Promise<
    IPresentation & {
        errors: Record<string, string[]>;
    }
> => {
    return httpDelete(`${BASE_PRESENTATIONS_URL}/shared`, {presentationId, userEmail}).catch((error) => {
        console.log(error);
        if (error.response && error.response.data && error.response.status === 403) {
            throw new Error(error.response.data.message);
        } else {
            return {errors: error.response.data};
        }
    });
};

export const fetchOwnerSharedPresentationsPaginated = (
    presentationId: string,
    pageNumber: number,
    pageSize: number
): Promise<IPaginatedResult<IPresentation>> => {
    return httpGet(`${BASE_PRESENTATIONS_URL}/${presentationId}/shared/${pageSize}/${pageNumber}`).catch((error) => {
        console.log(error);
    });
};

export const updateSharedPresentation = (presentationId: string, userEmail: string): Promise<void> => {
    return httpPut(`${BASE_PRESENTATIONS_URL}/shared`, {presentationId, userEmail}).catch((error) => {
        console.log(error);
    });
};

export const patchPresentation = (presentationId: string, patch: IPresentationMeta): Promise<void> => {
    return httpPatch(`${BASE_PRESENTATIONS_URL}/${presentationId}`, patch).catch((error) => {
        console.log(error);
    });
};

export const generateAIPresentation = async (data: AIPresentationMetaData): Promise<{_id: string; name: string}> => {
    const {jobId} = await httpPost(`${BASE_PRESENTATIONS_URL}/import-ai`, data);
    return listenToJob<{_id: string; name: string}>(jobId);
};
